/**
 * Centralized Route Definitions
 *
 * Single source of truth for all application routes.
 * Each route includes its path AND metadata for AI navigation.
 *
 * When adding a new route:
 * 1. Add ONE entry here with path + metadata
 * 2. That's it! ROUTES and AI tools auto-update.
 *
 * Note: Routes with dynamic parameters (e.g., :id) are defined with the full pattern.
 */

export type RouteDefinition = {
  /** The route path (e.g., '/dashboard' or '/employees/:id') */
  path: string;
  /** Should this route be available to the AI agent for navigation? */
  aiNavigable: boolean;
  /** Display name for AI (lowercase, underscores for spaces) - required if aiNavigable: true */
  displayName?: string;
  /** Human-readable description of what this page is for - required if aiNavigable: true */
  description?: string;
};

/**
 * Route Definitions - Single source of truth
 * Define path + metadata in ONE place
 */
export const ROUTE_DEFINITIONS = {
  // Root
  HOME: { path: '/', aiNavigable: false },

  // Core pages
  DASHBOARD: {
    path: '/dashboard',
    aiNavigable: true,
    displayName: 'dashboard',
    description: 'home/overview',
  },
  TASKS: {
    path: '/tasks',
    aiNavigable: true,
    displayName: 'tasks',
    description: 'to-do items/action items',
  },
  TASKS_DETAIL: {
    path: '/tasks/:requestId',
    aiNavigable: false, // Needs task ID
  },
  TASKS_GROUP_CHANGE_REVIEW: {
    path: '/tasks/:requestId/review',
    aiNavigable: false, // Needs task ID
  },
  TASKS_GROUP_CHANGE_SUB_RESOURCES: {
    path: '/tasks/:requestId/review/sub-resources/:appId/:groupCtx',
    aiNavigable: false,
  },
  NOTIFICATION_CENTER: {
    path: '/notification-center',
    aiNavigable: true,
    displayName: 'notification_center',
    description: 'notifications/alerts',
  },
  SETTINGS: {
    path: '/settings',
    aiNavigable: false,
  },
  SETTINGS_COMPANY_PROFILE: {
    path: '/settings/company',
    aiNavigable: true,
    displayName: 'settings_company_profile',
    description: 'company profile settings',
  },
  SETTINGS_BILLING: {
    path: '/settings/billing',
    aiNavigable: true,
    displayName: 'settings_billing',
    description: 'billing settings',
  },
  SETTINGS_BILLING_ADD_PAYMENT_METHOD: {
    path: '/settings/billing?modal=add-payment-method',
    aiNavigable: true,
    displayName: 'settings_billing_add_payment_method',
    description: 'billing settings - opens the add payment method modal',
  },
  SETTINGS_CATEGORY_CHAMPIONS: {
    path: '/settings/category-champions',
    aiNavigable: true,
    displayName: 'settings_category_champions',
    description: 'category champions settings',
  },
  SETTINGS_INTEGRATIONS: {
    path: '/settings/integrations',
    aiNavigable: true,
    displayName: 'settings_integrations',
    description: 'integrations settings',
  },
  SETTINGS_WEBHOOKS: {
    path: '/settings/webhooks',
    aiNavigable: true,
    displayName: 'settings_webhooks',
    description: 'outbound webhooks settings',
  },
  SETTINGS_DIVISIONS: {
    path: '/settings/divisions',
    aiNavigable: true,
    displayName: 'settings_divisions',
    description: 'divisions settings - divisions and their employees',
  },
  SETTINGS_DEVICE: {
    path: '/settings/device',
    aiNavigable: true,
    displayName: 'settings_device',
    description:
      'device settings including MDM and ABM (Apple Business Manager) configuration',
  },
  PLANS: {
    path: '/plans',
    aiNavigable: true,
    displayName: 'plans',
    description: 'pricing/plan information',
  },
  REQUEST_SUPPORT: {
    path: '/request-support',
    aiNavigable: false, // Action, not navigation destination
  },
  ORGANIZATION_SWITCHER: {
    path: '/switcher',
    aiNavigable: false, // Conditional feature
  },

  // People & Employees
  EMPLOYEES: {
    path: '/employees',
    aiNavigable: true,
    displayName: 'people',
    description: 'employees/users',
  },
  EMPLOYEES_DETAIL: {
    path: '/employees/:employeeId',
    aiNavigable: false, // Needs employee ID
  },
  EMPLOYEES_HRIS_DETAIL: {
    path: '/employees/hris/:hrisEmployeeId',
    aiNavigable: false, // Needs HRIS employee ID
  },
  ACTIVE_EMPLOYEES: {
    path: '/active-employees',
    aiNavigable: true,
    displayName: 'active_employees',
    description: 'active employees',
  },
  INACTIVE_EMPLOYEES: {
    path: '/inactive-employees',
    aiNavigable: true,
    displayName: 'inactive_employees',
    description: 'inactive employees',
  },
  EMPLOYEES_STATUS_REVIEW: {
    path: '/employees/status-review',
    aiNavigable: false, // Specific workflow
  },
  GROUPS: {
    path: '/groups',
    aiNavigable: true,
    displayName: 'groups',
    description: 'employee groups/teams',
  },
  GROUPS_DETAIL: {
    path: '/groups/:groupId',
    aiNavigable: false, // Needs group ID
  },
  GROUPS_NEW: {
    path: '/groups/new',
    aiNavigable: true,
    displayName: 'create_group',
    description: 'create new employee group/team',
  },

  // Applications
  APPLICATIONS: {
    path: '/applications',
    aiNavigable: true,
    displayName: 'applications',
    description: 'software/apps',
  },
  APPLICATIONS_DETAIL: {
    path: '/applications/:applicationSlug',
    aiNavigable: false, // Needs application slug
  },

  // Onboarding & Offboarding
  ONBOARDINGS_NEW: {
    path: '/onboardings/new',
    aiNavigable: true,
    displayName: 'new_onboarding',
    description: 'create employee onboarding',
  },
  ONBOARDINGS_HRIS_NEW: {
    path: '/onboardings/hris/new',
    aiNavigable: false, // Specific integration workflow
  },
  OFFBOARDINGS_NEW: {
    path: '/offboardings/new',
    aiNavigable: true,
    displayName: 'new_offboarding',
    description: 'create employee offboarding',
  },
  OFFBOARDINGS_HRIS_NEW: {
    path: '/offboardings/hris/new',
    aiNavigable: false, // Specific integration workflow
  },
  GROUP_CHANGES_HRIS_NEW: {
    path: '/group-changes/hris/new',
    aiNavigable: false, // Specific integration workflow
  },
  LEAVE_NEW: {
    path: '/leave/new',
    aiNavigable: false, // Needs an employeeId query param
  },
  LEAVE_RETURN_TO_WORK: {
    path: '/leave/return-to-work',
    aiNavigable: false, // Needs employeeId and request id query params
  },

  // Assets
  ASSETS: {
    path: '/assets',
    aiNavigable: true,
    displayName: 'assets',
    description: 'devices/hardware',
  },
  ASSETS_NEW: {
    path: '/assets/new',
    aiNavigable: true,
    displayName: 'new_asset',
    description: 'create new asset',
  },
  ASSETS_DETAIL: {
    path: '/assets/:assetId',
    aiNavigable: false, // Needs asset ID
  },
  ASSETS_EDIT: {
    path: '/assets/:assetId/edit',
    aiNavigable: false, // Needs asset ID + edit mode
  },
  DEVICE_MANAGEMENT: {
    path: '/device-management',
    aiNavigable: true,
    displayName: 'device_management',
    description: 'manage devices',
  },

  // Security
  SECURITY: {
    path: '/security',
    aiNavigable: true,
    displayName: 'security',
    description: 'security overview and management',
  },
  PASSWORD_BREACHES: {
    path: '/password-breaches',
    aiNavigable: true,
    displayName: 'password_breaches',
    description: 'security breaches/threats',
  },
  SECURITY_SOLUTIONS_ANTIVIRUS: {
    path: '/security-solutions/antivirus',
    aiNavigable: true,
    displayName: 'antivirus',
    description: 'antivirus security',
  },
  SECURITY_SOLUTIONS_EMAIL_SECURITY: {
    path: '/security-solutions/email-security',
    aiNavigable: true,
    displayName: 'email_security',
    description: 'email security settings',
  },

  // Storefront & Orders
  GET_HARDWARE: {
    path: '/get-hardware',
    aiNavigable: true,
    displayName: 'storefront',
    description: 'purchase or assign hardware/cart',
  },
  TRACK_ORDERS: {
    path: '/track-orders',
    aiNavigable: true,
    displayName: 'track_orders',
    description: 'order tracking/order status',
  },
  TRACK_ORDERS_DETAIL: {
    path: '/track-orders/:friendlyId',
    aiNavigable: false, // Needs order ID
  },
  QUOTE_DETAILS: {
    path: '/quotes/:friendlyId',
    aiNavigable: false, // Needs quote ID
  },

  // Shipments
  SHIP_LAPTOP: {
    path: '/ship-laptop',
    aiNavigable: true,
    displayName: 'ship_laptop',
    description: 'ship device to employee',
  },
  SHIPMENTS_QR_CODE: {
    path: '/shipments/qr-code',
    aiNavigable: true,
    displayName: 'qr_code_shipments',
    description: 'track laptop shipments using QR codes',
  },

  // Marketplace
  DISCOVER: {
    path: '/discover',
    aiNavigable: true,
    displayName: 'marketplace',
    description: 'discover new services',
  },

  // Employee Center
  AI_CHAT: {
    path: '/AIChat',
    aiNavigable: true,
    displayName: 'gigawatt',
    description: 'Gigawatt/AI chat/assistant',
  },
  HELPFUL_LINKS: {
    path: '/helpful-links',
    aiNavigable: true,
    displayName: 'helpful_links',
    description: 'resources/links',
  },
  SUPPORT_CENTER: {
    path: '/support-center',
    aiNavigable: true,
    displayName: 'support_center',
    description: 'help/support',
  },

  // Integrations
  INTEGRATIONS_CONNECT: {
    path: '/integrations/connect/:applicationKey',
    aiNavigable: false, // System workflow
  },
  INTEGRATIONS_HRIS_CONNECT: {
    path: '/integrations/hris/connect/:applicationKey',
    aiNavigable: false, // System workflow
  },
  INTEGRATIONS_OAUTH_CALLBACK: {
    path: '/integrations/oauth/callback',
    aiNavigable: false, // System workflow
  },
  INTEGRATIONS_MIGRATION_JUSTWORKS: {
    path: '/integrations/migration/justworks',
    aiNavigable: false, // System workflow
  },
  INTEGRATIONS_SETUP_JUSTWORKS: {
    path: '/integrations/setup/justworks',
    aiNavigable: false, // System workflow
  },
  INTEGRATIONS_SETUP_TRINET: {
    path: '/integrations/setup/trinet',
    aiNavigable: false, // System workflow
  },
  ADP_TERMS: {
    path: '/integrations/setup/adp-terms',
    aiNavigable: false, // System workflow
  },

  // Secrets / Credential sharing
  SECRET_SHARE: {
    path: '/secrets/share/:secretId',
    aiNavigable: false, // Needs secret ID
  },

  // SDK Test Page (unauthenticated, feature-flagged)
  SDK_TEST: {
    path: '/sdk-test',
    aiNavigable: false, // Internal test route
  },

  // Catch-all
  CATCH_ALL: {
    path: '*',
    aiNavigable: false, // Pattern, not destination
  },

  // Special pages
  WELCOME: {
    path: '/welcome',
    aiNavigable: false, // Forced workflow
  },
  MAINTENANCE: {
    path: '/maintenance',
    aiNavigable: false, // System controlled
  },
};

/**
 * Derive ROUTES for router usage
 * Extracts just the paths: { DASHBOARD: '/dashboard', ... }
 * Type-safe: accessing non-existent routes will cause TypeScript errors
 */
export const ROUTES = (() => {
  const result = {} as {
    [K in keyof typeof ROUTE_DEFINITIONS]: (typeof ROUTE_DEFINITIONS)[K]['path'];
  };

  for (const key in ROUTE_DEFINITIONS) {
    if (Object.prototype.hasOwnProperty.call(ROUTE_DEFINITIONS, key)) {
      result[key as keyof typeof ROUTE_DEFINITIONS] =
        ROUTE_DEFINITIONS[key as keyof typeof ROUTE_DEFINITIONS].path;
    }
  }

  return result;
})() as {
  readonly [K in keyof typeof ROUTE_DEFINITIONS]: (typeof ROUTE_DEFINITIONS)[K]['path'];
};

export type GroupCtx = 'added' | 'removed';

/**
 * Route builder helpers for parameterized routes
 * Use these to construct routes with dynamic segments
 */
export const ROUTE_BUILDERS = {
  // Tasks
  taskDetails: (requestId: string) => `/tasks/${requestId}`,
  taskGroupChangeReview: (requestId: string) => `/tasks/${requestId}/review`,
  taskGroupChangeSubResources: (
    requestId: string,
    appId: string,
    groupCtx: GroupCtx
  ) => `/tasks/${requestId}/review/sub-resources/${appId}/${groupCtx}`,

  // Employees
  employeeProfile: (employeeId: string) => `/employees/${employeeId}`,
  hrisEmployeeProfile: (hrisEmployeeId: string) =>
    `/employees/hris/${hrisEmployeeId}`,

  // Groups
  groupDetails: (groupId: string) => `/groups/${groupId}`,

  // Applications
  applicationDetails: (applicationSlug: string) =>
    `/applications/${applicationSlug}`,

  // Assets
  assetDetails: (assetId: string) => `/assets/${assetId}`,
  assetEdit: (assetId: string) => `/assets/${assetId}/edit`,

  // Orders
  orderDetails: (friendlyId: string) => `/track-orders/${friendlyId}`,

  // Secrets
  secretShare: (secretId: string) => `/secrets/share/${secretId}`,

  // Webhooks
  webhookDetails: (webhookId: string) => `/settings/webhooks/${webhookId}`,

  // Divisions
  divisionDetails: (divisionId: string) => `/settings/divisions/${divisionId}`,

  // Integrations
  integrationsConnect: (applicationKey: string) =>
    `/integrations/connect/${applicationKey}`,
  hrisIntegrationsConnect: (applicationKey: string) =>
    `/integrations/hris/connect/${applicationKey}`,
} as const;

/**
 * Internal routes (Electric employees only)
 * Base path: /internal/*
 */
export const INTERNAL_ROUTES = {
  WILDCARD: '/internal/*',
  BASE: '/internal',
  HOME: '/internal/',
  APPLICATIONS: '/internal/applications',
  APPLICATIONS_NEW: '/internal/applications/new',
  WAREHOUSE: '/internal/warehouse',
  ORDER_MANAGEMENT: '/internal/order-management',
  CATALOG_PRODUCTS: '/internal/catalog/products',
  BILLING: '/internal/billing',
  BILLING_NEW_QUOTE: '/internal/billing/new-quote',
  INVOICES_NEW: '/internal/invoices/new',
  ORGANIZATION_MANAGEMENT: '/internal/organization-management',
} as const;

/**
 * Embedded routes (iFrame integration)
 * Base path: /embed/*
 */
export const EMBEDDED_ROUTES = {
  WILDCARD: '/embed/*',
  BASE: '/embed',
  PURCHASE_HARDWARE: '/embed/purchase-hardware', // legacy embed route
  GET_HARDWARE: '/embed/get-hardware',
  SHIP_LAPTOP: '/embed/ship-laptop',
} as const;

/**
 * Legacy redirect paths
 * Routes that redirect to new locations for backwards compatibility
 * Note: These are the OLD paths that should redirect
 */
export const LEGACY_REDIRECT_PATHS = {
  SECURITY_SOLUTIONS: '/security-solutions', // -> /discover
  SECURITY_SOLUTIONS_THREATDOWN: '/security-solutions/threatdown', // -> /security-solutions/antivirus
  SECURITY_SOLUTIONS_HEC: '/security-solutions/hec', // -> /security-solutions/email-security
  HARDWARE: '/hardware', // -> /get-hardware
  PURCHASE_HARDWARE: '/purchase-hardware', // -> /get-hardware
  SHIP_DEVICE: '/ship-device', // -> /ship-laptop
  SCHEDULED_TASKS: '/scheduled-tasks', // -> /settings/company
} as const;

/**
 * Type helper: Extract all route values as a union type
 */
export type RouteValue = (typeof ROUTES)[keyof typeof ROUTES];
export type InternalRouteValue =
  (typeof INTERNAL_ROUTES)[keyof typeof INTERNAL_ROUTES];
export type EmbeddedRouteValue =
  (typeof EMBEDDED_ROUTES)[keyof typeof EMBEDDED_ROUTES];
